// v2/components/Header.js
import React, { useRef, useState, useCallback, useEffect } from 'react';
import {
  ShoppingCart, ChevronDown, FileText,
  Sparkles, User, ClipboardList, CreditCard, Package, Wallet, LogOut,
} from 'lucide-react';
import { HEADER_NAV, USER_MENU } from '../constants/nav';
import useClickOutside from '../hooks/useClickOutside';
import CartPanel from './CartPanel';
import useCari from '../hooks/useCari';
import MobileNav from './MobileNav';
import HeaderSearch from './HeaderSearch';
import styles from './Header.module.css';

const LOGO_SRC = '/asotomotivlogo.png';

const ICONS = {
  Sparkles, User, ClipboardList, CreditCard,
  Package, Wallet, ShoppingCart, LogOut, FileText,
};

/** Sepet rozeti: sayi degisince kisa zipla */
function useBump(value) {
  const [on, setOn] = useState(false);
  const prev = useRef(value);
  const timer = useRef(null);

  useEffect(() => {
    if (value !== prev.current && value > 0) {
      setOn(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setOn(false), 420);
    }
    prev.current = value;
  }, [value]);

  useEffect(() => () => clearTimeout(timer.current), []);
  return on;
}

export default function Header({
  searchTerm = '',
  onSearchChange = () => {},
  suggest = true,
  cart = null,
  eurRate = 0,
  customerName = null,
  customerTitle = null,
  customerCode = null,
  onMenuSelect = () => {},
}) {
  // Cari bilgisi oturumdan/API'den gelir; prop verilirse o onceliklidir.
  const cari = useCari();
  const adi = customerName || cari.unvan || cari.adSoyad || '—';
  const unvani = customerTitle || cari.adSoyad || '';
  const kodu = customerCode || cari.kod || '';
  const [logoOk, setLogoOk] = useState(true);
  const [boschOk, setBoschOk] = useState(true);
  const [openPanel, setOpenPanel] = useState(null); // 'user' | 'cart' | null
  const [drawerOpen, setDrawerOpen] = useState(false);

  const userPanel = useRef(null);
  const userBtn = useRef(null);
  const cartPanel = useRef(null);
  const cartBtn = useRef(null);

  const close = useCallback(() => setOpenPanel(null), []);
  useClickOutside(userPanel, userBtn, close, openPanel === 'user');
  useClickOutside(cartPanel, cartBtn, close, openPanel === 'cart');

  // ESC ile kapat
  useEffect(() => {
    if (!openPanel) return undefined;
    const esc = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [openPanel, close]);

  const toggle = (name) => setOpenPanel((cur) => (cur === name ? null : name));

  const unitCount = cart ? cart.totals.unitCount : 0;
  const bump = useBump(unitCount);
  const initial = (adi || '?').trim().charAt(0).toLocaleUpperCase('tr');

  return (
    <header className={styles.header}>
      <a className={styles.brand} href="/v2" aria-label="AS Otomotiv">
        {logoOk ? (
          <img className={styles.logoImg} src={LOGO_SRC} alt="AS Otomotiv"
               onError={() => setLogoOk(false)} />
        ) : (
          <span className={styles.logoText}>
            <span className={styles.logoTop}>AS OTOMOTİV</span>
            <span className={styles.logoBottom}>B2B PORTAL</span>
          </span>
        )}
        <span className={styles.logoSep} />
        {boschOk && (
          <img className={styles.boschImg} src="/bosch.png" alt="Bosch Yetkili Satıcı"
               onError={() => setBoschOk(false)} />
        )}
      </a>

      <HeaderSearch value={searchTerm} onChange={onSearchChange} suggest={suggest} />

      <nav className={`${styles.nav} ${styles.deskOnly}`} aria-label="Ana menü">
        {HEADER_NAV.map((item) => {
          const Icon = ICONS[item.icon] || User;
          return (
            <button
              key={item.id} type="button"
              className={`${styles.navItem} ${item.accent ? styles.navAccent : ''}`}
              onClick={() => onMenuSelect(item.id)}
            >
              <Icon size={19} strokeWidth={1.7} />
              <span className={styles.navLabel}>{item.label}</span>
            </button>
          );
        })}

        <div className={styles.dropWrap}>
          <button
            ref={cartBtn} type="button"
            className={`${styles.navItem} ${openPanel === 'cart' ? styles.navOn : ''}`}
            onClick={() => toggle('cart')}
            aria-expanded={openPanel === 'cart'}
            aria-label={`Sepetim, ${unitCount} adet`}
          >
            <span className={styles.navIconWrap}>
              <ShoppingCart size={19} strokeWidth={1.7} />
              {unitCount > 0 && (
                <span className={`${styles.badge} ${bump ? styles.badgeBump : ''}`}>
                  {unitCount}
                </span>
              )}
            </span>
            <span className={styles.navLabel}>Sepetim</span>
          </button>
          {openPanel === 'cart' && cart && (
            <div ref={cartPanel} className={`${styles.drop} ${styles.dropCart}`}>
              <CartPanel
                cart={cart} eurRate={eurRate}
                onCheckout={() => { close(); onMenuSelect('checkout'); }}
                onViewCart={() => { close(); onMenuSelect('cart'); }}
              />
            </div>
          )}
        </div>

      </nav>

      <span className={`${styles.divider} ${styles.deskOnly}`} />

      <div className={`${styles.dropWrap} ${styles.deskOnly}`}>
        <button
          ref={userBtn} type="button"
          className={`${styles.userBtn} ${openPanel === 'user' ? styles.active : ''}`}
          onClick={() => toggle('user')}
          aria-expanded={openPanel === 'user'}
        >
          <span className={styles.avatar}>{initial}</span>
          <span className={styles.userText}>
            <span className={styles.userName}>{adi}</span>
            <span className={styles.userLabel}>Cari: {kodu}</span>
          </span>
          <ChevronDown size={16}
            className={`${styles.chev} ${openPanel === 'user' ? styles.chevOpen : ''}`} />
        </button>
        {openPanel === 'user' && (
          <div ref={userPanel} className={styles.drop} role="menu">
            <div className={styles.userCard}>
              <span className={styles.userCardName}>{adi}</span>
              <span className={styles.userCardTitle}>{unvani}</span>
              <span className={styles.userCardCode}>Cari Kod: {kodu}</span>
            </div>
            {USER_MENU.map((item) => {
              const Icon = ICONS[item.icon] || User;
              return (
                <button
                  key={item.id} type="button" role="menuitem"
                  className={`${styles.menuItem} ${item.danger ? styles.menuDanger : ''}`}
                  onClick={() => { close(); onMenuSelect(item.id); }}
                >
                  <Icon size={15} strokeWidth={1.8} />
                  {item.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <MobileNav
        cartCount={unitCount}
        customerName={adi}
        customerTitle={unvani}
        customerCode={kodu}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        onSelect={(id) => {
          if (id === 'cart') { setDrawerOpen(false); toggle('cart'); return; }
          onMenuSelect(id);
        }}
      />
    </header>
  );
}
